import { useParams, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ArrowLeft, RotateCcw, RefreshCw, Copy, Download, Loader2, X, Tag, ImageIcon, ExternalLink, Camera, Image as ImageLucide, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tasksApi, tagsApi, type TaskDetail as TaskDetailData, type Tag as TagType } from "@/api";
import { alibabaApi, type PipelineStep } from "@/api/alibaba";
import { PipelineTimeline } from "@/components/pipeline/PipelineTimeline";
import { PipelineGantt } from "@/components/pipeline/PipelineGantt";
import { PipelineSummary } from "@/components/pipeline/PipelineSummary";

function HeadingTree({ data }: { data: any[] }) {
  if (!data || !Array.isArray(data)) return null;
  return (
    <ul className="space-y-1 pl-4 border-l border-border">
      {data.map((item, i) => (
        <li key={i}>
          <span className="text-xs font-mono text-muted-foreground">&lt;{item.tag}&gt;</span>{" "}
          <span className="text-sm">{item.text}</span>
          {item.children?.length > 0 && <HeadingTree data={item.children} />}
        </li>
      ))}
    </ul>
  );
}

function NavTree({ items, depth = 0 }: { items: any[]; depth?: number }) {
  if (!items || items.length === 0) return null;
  const depthLabels = ['', '  ', '    ', '      '];
  const indent = depthLabels[Math.min(depth, 3)] || '        ';
  return (
    <ul className={`space-y-0.5 ${depth > 0 ? 'pl-4 border-l border-border/50' : ''}`}>
      {items.map((item: any, i: number) => {
        const label = item.label || item.text || item.name || '';
        const url = item.url || item.href || '';
        const children = item.children || [];
        return (
          <li key={i}>
            <div className="flex items-center gap-2 py-0.5">
              {depth > 0 && <span className="text-muted-foreground text-xs">{'└'}</span>}
              <span className="text-sm font-medium">{label}</span>
              {url && url !== '#' && !url.startsWith('javascript:') && (
                <span className="text-xs text-muted-foreground font-mono truncate max-w-[300px]">{url}</span>
              )}
              {children.length > 0 && (
                <span className="text-xs text-muted-foreground">({children.length})</span>
              )}
            </div>
            {children.length > 0 && <NavTree items={children} depth={depth + 1} />}
          </li>
        );
      })}
    </ul>
  );
}

// Crawl strategy display name mapping
const STRATEGY_LABELS: Record<string, string> = {
  basic_ua_rotate: "基本抓取",
  quickscraper_standard: "QuickScraper",
  browseract_tw: "BrowserAct TW",
  browseract_us: "BrowserAct US",
};

function getStrategyLabel(strategy?: string | null): string {
  if (!strategy) return "—";
  return STRATEGY_LABELS[strategy] || strategy;
}

function TaskTags({ taskId }: { taskId: string }) {
  const [input, setInput] = useState("");
  const queryClient = useQueryClient();

  const { data: tags = [] } = useQuery<TagType[]>({
    queryKey: ["task-tags", taskId],
    queryFn: async () => (await tagsApi.forTask(taskId)).data || [],
  });

  const { data: allTags = [] } = useQuery<TagType[]>({
    queryKey: ["all-tags"],
    queryFn: async () => (await tagsApi.list()).data || [],
  });

  const addMutation = useMutation({
    mutationFn: (tagName: string) => tagsApi.link(taskId, tagName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-tags", taskId] });
      queryClient.invalidateQueries({ queryKey: ["all-tags"] });
      setInput("");
    },
  });

  const removeMutation = useMutation({
    mutationFn: (tagId: number) => tagsApi.unlink(taskId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-tags", taskId] });
      queryClient.invalidateQueries({ queryKey: ["all-tags"] });
    },
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && input.trim()) {
      e.preventDefault();
      addMutation.mutate(input.trim());
    }
  };

  // Suggestions: tags that exist but not yet linked to this task
  const suggestions = allTags.filter(t => !tags.some(tt => tt.id === t.id));
  const filtered = input.trim()
    ? suggestions.filter(t => t.tag_name.toLowerCase().includes(input.toLowerCase())).slice(0, 5)
    : [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {tags.map(t => (
          <Badge key={t.id} variant="secondary" className="gap-1 pr-1">
            {t.tag_name}
            <button onClick={() => removeMutation.mutate(t.id)} className="ml-0.5 hover:text-destructive">
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {tags.length === 0 && <span className="text-sm text-muted-foreground">no tags</span>}
      </div>
      <div className="relative">
        <Input
          placeholder="type tag + Enter..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-8 text-sm"
        />
        {filtered.length > 0 && (
          <div className="absolute z-10 top-9 left-0 right-0 bg-popover border rounded-md shadow-md max-h-[120px] overflow-y-auto">
            {filtered.map(t => (
              <div key={t.id} className="px-3 py-1.5 text-sm cursor-pointer hover:bg-accent" onClick={() => addMutation.mutate(t.tag_name)}>
                {t.tag_name} <span className="text-muted-foreground text-xs">({t.usage_count})</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ScoreBar({ label, score }: { label: string; score: number }) {
  const color = score >= 80 ? "bg-green-500" : score >= 60 ? "bg-yellow-500" : "bg-red-500";
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span className="font-bold">{score}</span>
      </div>
      <div className="h-2 rounded-full bg-muted">
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

function PipelineTab({ taskId }: { taskId: string }) {
  const queryClient = useQueryClient();

  const { data: steps = [], isLoading: pipelineLoading } = useQuery<PipelineStep[]>({
    queryKey: ['task-pipeline', taskId],
    queryFn: async () => {
      const res = await alibabaApi.getTaskPipeline(taskId);
      return res.data ?? [];
    },
    enabled: !!taskId,
    refetchInterval: 15_000,
  });

  const handleRetry = (stepId: string) => {
    alibabaApi.retryPipelineStep(taskId, stepId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['task-pipeline', taskId] });
        toast.success(`已重試 ${stepId}`);
      })
      .catch((err: Error) => toast.error(`重試失敗: ${err.message}`));
  };

  if (pipelineLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (steps.length === 0) {
    return <p className="text-sm text-muted-foreground py-8 text-center">尚無 Pipeline 資料</p>;
  }

  return (
    <div className="space-y-6">
      <PipelineSummary steps={steps} />
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">高鐵式時間軸</CardTitle></CardHeader>
        <CardContent className="p-0">
          <PipelineTimeline steps={steps} onRetry={handleRetry} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">甘特圖列表</CardTitle></CardHeader>
        <CardContent className="p-0">
          <PipelineGantt steps={steps} onRetry={handleRetry} />
        </CardContent>
      </Card>
    </div>
  );
}

// ── iframe description status labels ──
const IFRAME_STATUS_CONFIG: Record<string, { label: string; color: string; message: string }> = {
  fetched: { label: '已取得', color: 'bg-green-600', message: '' },
  no_url: { label: '無 iframe', color: 'bg-gray-500', message: '此產品無 iframe 描述' },
  captcha: { label: '驗證碼', color: 'bg-red-500', message: '抓取被驗證碼攔截' },
  captcha_persistent: { label: 'CAPTCHA 持續', color: 'bg-orange-500', message: '多次重試仍被 CAPTCHA 攔截，無法取得內容' },
  timeout: { label: '超時', color: 'bg-amber-500', message: '抓取超時' },
  failed: { label: '失敗', color: 'bg-red-500', message: '抓取失敗' },
  pending: { label: '排隊中', color: 'bg-blue-500', message: '等待執行' },
};

function IframeTab({ content, taskId, productUrl }: { content: Record<string, unknown>; taskId: string; productUrl?: string | null }) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const iframeRecrawlMutation = useMutation({
    mutationFn: () => alibabaApi.retryIframePipeline(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-detail', taskId] });
      toast.success('iframe 重試已啟動');
    },
    onError: (err: Error) => toast.error(`重試失敗: ${err.message}`),
  });

  const status = (content.desc_iframe_status as string) || null;
  const iframeUrl = content.desc_iframe_url as string | null;
  const screenshotUrl = content.desc_iframe_screenshot_url as string | null;
  const imageCount = (content.iframe_image_count as number) || 0;

  // Parse iframe_image_urls — could be JSON string or array
  let iframeImages: string[] = [];
  try {
    const raw = content.iframe_image_urls;
    if (Array.isArray(raw)) {
      iframeImages = raw as string[];
    } else if (typeof raw === 'string' && raw.startsWith('[')) {
      iframeImages = JSON.parse(raw) as string[];
    }
  } catch { /* ignore */ }

  const statusCfg = status ? IFRAME_STATUS_CONFIG[status] : null;
  const canRetry = status === 'captcha' || status === 'timeout' || status === 'failed';
  const isCaptchaPersistent = status === 'captcha_persistent';

  // If no iframe URL at all
  if (!iframeUrl && !status) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">尚未執行 iframe 抓取</p>
          <p className="text-xs text-muted-foreground mt-1">需先完成產品頁爬取 + 解析</p>
        </CardContent>
      </Card>
    );
  }

  if (status === 'no_url') {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <ImageLucide className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">此產品無 iframe 描述</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Lightbox */}
      <Dialog open={!!lightboxUrl} onOpenChange={() => setLightboxUrl(null)}>
        <DialogContent className="max-w-4xl p-2">
          {lightboxUrl && (
            <img
              src={lightboxUrl}
              alt="Full size"
              className="w-full h-auto max-h-[85vh] object-contain rounded"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Status + iframe URL */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            iframe 描述
            {statusCfg && (
              <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
            )}
            {imageCount > 0 && (
              <Badge variant="secondary">{imageCount} 張圖片</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {iframeUrl && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border">
              <a
                href={iframeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-mono text-primary hover:underline truncate flex-1"
              >
                {iframeUrl}
              </a>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 shrink-0"
                onClick={() => {
                  navigator.clipboard.writeText(iframeUrl);
                  toast.success('iframe URL 已複製');
                }}
              >
                <Copy className="h-3 w-3 mr-1" />複製
              </Button>
              <a href={iframeUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="sm" className="h-7 shrink-0">
                  <ExternalLink className="h-3 w-3 mr-1" />開啟
                </Button>
              </a>
            </div>
          )}

          {/* Error status + retry */}
          {statusCfg?.message && status !== 'fetched' && (
            <div className="flex items-center gap-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
              <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
              <span className="text-sm">{statusCfg.message}</span>
              {canRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto shrink-0"
                  onClick={() => iframeRecrawlMutation.mutate()}
                  disabled={iframeRecrawlMutation.isPending}
                >
                  {iframeRecrawlMutation.isPending ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3 mr-1" />
                  )}
                  重試抓取
                </Button>
              )}
            </div>
          )}

          {/* captcha_persistent: show direct Alibaba link */}
          {isCaptchaPersistent && productUrl && (
            <div className="flex items-center gap-3 p-3 rounded-lg border border-orange-500/30 bg-orange-500/5">
              <AlertCircle className="h-4 w-4 text-orange-500 shrink-0" />
              <span className="text-sm text-orange-700">CAPTCHA 持續阻擋，無法自動取得 iframe 內容</span>
              <a
                href={productUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto shrink-0"
              >
                <Button variant="outline" size="sm" className="border-orange-400 text-orange-700 hover:bg-orange-50">
                  <ExternalLink className="h-3 w-3 mr-1" />
                  直接前往阿里巴巴 ↗
                </Button>
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Screenshot */}
      {screenshotUrl && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Camera className="h-4 w-4" />
              iframe 截圖
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="cursor-pointer rounded-lg overflow-hidden border bg-white"
              onClick={() => setLightboxUrl(screenshotUrl)}
            >
              <img
                src={screenshotUrl}
                alt="iframe screenshot"
                className="w-full h-auto max-h-[600px] object-contain"
                loading="lazy"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">點擊圖片可放大查看</p>
          </CardContent>
        </Card>
      )}

      {/* iframe Image Gallery */}
      {iframeImages.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              iframe 圖片 Gallery ({iframeImages.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {iframeImages.map((url, idx) => (
                <div
                  key={idx}
                  className="group relative rounded-lg overflow-hidden border border-border bg-white cursor-pointer"
                  onClick={() => setLightboxUrl(url)}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={url}
                      alt={`iframe image ${idx + 1}`}
                      className="w-full h-full object-contain transition-transform duration-200 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  <div className="p-1.5 text-center">
                    <span className="text-xs text-muted-foreground">#{idx + 1}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* iframe HTML content render */}
      {(content.desc_iframe_html as string) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">產品描述 HTML</CardTitle>
          </CardHeader>
          <CardContent>
            <iframe
              srcDoc={content.desc_iframe_html as string}
              sandbox="allow-same-origin"
              className="w-full min-h-[500px] border rounded-lg bg-white"
              title="Product Description"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function TaskDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (tab: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (tab === "overview") next.delete("tab");
      else next.set("tab", tab);
      return next;
    }, { replace: true });
  };
  const queryClient = useQueryClient();

  // Accordion / sub-tab state (must be before useQuery that references htmlSubTab)
  const [htmlRawOpen, setHtmlRawOpen] = useState(false);
  const [htmlBaOpen, setHtmlBaOpen] = useState(false);
  const [htmlSubTab, setHtmlSubTab] = useState<'qs' | 'ba' | 'iframe'>('ba');
  const [mdSubTab, setMdSubTab] = useState<'qs' | 'ba' | 'iframe'>('ba');

  // Fetch task with all details (content + analysis + retries + workflow)
  // Note: html_content/html_full/html_ssr are stripped server-side (>100KB) to prevent 3MB+ responses.
  // Use the /html endpoint for lazy-loading (see html tab below).
  const { data: detail, isLoading, isError, error: queryError } = useQuery<TaskDetailData>({
    queryKey: ["task-detail", id],
    queryFn: async () => {
      const res = await tasksApi.get(id!);
      return res.data!;
    },
    enabled: !!id,
  });

  // Lazy-load HTML (fetched only when html tab is active to avoid 3MB+ page load)
  const { data: lazyHtmlData, isLoading: htmlLoading } = useQuery({
    queryKey: ['task-html', id, htmlSubTab],
    queryFn: async () => {
      const res = await tasksApi.getHtml(id!, htmlSubTab);
      return res.data?.html || '';
    },
    enabled: activeTab === 'html' && !!id,
    staleTime: 10 * 60 * 1000,
  });

  const task = detail?.task;
  const content = detail?.content as Record<string, any> | undefined;
  const seoAnalysis = detail?.analysis as Record<string, any> | undefined;

  // Alibaba product page detection
  const isAlibabaProduct = task?.url?.includes('alibaba.com/product-detail/') ?? false;

  const retryMutation = useMutation({
    mutationFn: () => tasksApi.retry(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-detail", id] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("任務已重新提交");
    },
    onError: (err: Error) => toast.error(`重試失敗: ${err.message}`),
  });

  const rescrapeMutation = useMutation({
    mutationFn: () => tasksApi.create({ url: task!.url }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("已建立新的抓取任務");
    },
    onError: (err: Error) => toast.error(`重新抓取失敗: ${err.message}`),
  });

  const handleRetry = () => { if (task && confirm("確定要重新執行？")) retryMutation.mutate(); };
  const handleRescrape = () => { if (task && confirm("確定要重新抓取？")) rescrapeMutation.mutate(); };

  // Parse JSON fields safely
  const parseJson = (str?: string | null, fallback: any = []) => {
    if (!str) return fallback;
    try {
      return JSON.parse(str);
    } catch {
      // If it's a non-JSON string, return as single-item array for tags
      if (typeof str === "string" && str.trim()) return [str];
      return fallback;
    }
  };

  // H tags from new h_structure_json (array of {level, text}) or legacy fields
  const hStructure: Array<{ level: number; text: string }> = (() => {
    const raw = content?.h_structure_json;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') return parseJson(raw, []);
    return [];
  })();
  const h1Tags = hStructure.length > 0
    ? hStructure.filter((h: any) => h.level === 1).map((h: any) => h.text)
    : parseJson(content?.h1_tags, []);
  const h2Tags = hStructure.length > 0
    ? hStructure.filter((h: any) => h.level === 2).map((h: any) => h.text)
    : parseJson(content?.h2_tags, []);
  const h3Tags = hStructure.length > 0
    ? hStructure.filter((h: any) => h.level === 3).map((h: any) => h.text)
    : parseJson(content?.h3_tags, []);
  const h4Tags = hStructure.length > 0
    ? hStructure.filter((h: any) => h.level === 4).map((h: any) => h.text)
    : parseJson(content?.h4_tags, []);
  const h5Tags = hStructure.length > 0
    ? hStructure.filter((h: any) => h.level === 5).map((h: any) => h.text)
    : parseJson(content?.h5_tags, []);
  const h6Tags = hStructure.length > 0
    ? hStructure.filter((h: any) => h.level === 6).map((h: any) => h.text)
    : parseJson(content?.h6_tags, []);
  const headingTree = hStructure.length > 0
    ? hStructure.map((h: any) => ({ tag: `h${h.level}`, text: h.text }))
    : parseJson(content?.h_structure_text, []);

  // Nav items from new nav_tree_json or legacy fields
  const navItems = (() => {
    const raw = content?.nav_tree_json;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') return parseJson(raw, []);
    return parseJson(content?.nav_json || content?.nav_structure_links, []);
  })();
  
  // Build social links from new social_links_json (object) or legacy individual fields
  const socialLinksObj = content?.social_links_json && typeof content.social_links_json === 'object'
    ? content.social_links_json as Record<string, string[]>
    : null;
  const socialLinks = socialLinksObj
    ? Object.entries(socialLinksObj).flatMap(([platform, urls]) =>
        (Array.isArray(urls) ? urls : []).map((url: string) => ({ platform, url }))
      )
    : [
        ...parseJson(content?.facebook_links, []).map((url: string) => ({ platform: "Facebook", url })),
        ...parseJson(content?.linkedin_links, []).map((url: string) => ({ platform: "LinkedIn", url })),
        ...parseJson(content?.twitter_links, []).map((url: string) => ({ platform: "Twitter", url })),
        ...parseJson(content?.instagram_links, []).map((url: string) => ({ platform: "Instagram", url })),
        ...parseJson(content?.youtube_links, []).map((url: string) => ({ platform: "YouTube", url })),
        ...parseJson(content?.pinterest_links, []).map((url: string) => ({ platform: "Pinterest", url })),
        ...parseJson(content?.whatsapp_links, []).map((url: string) => ({ platform: "WhatsApp", url })),
        ...parseJson(content?.skype_links, []).map((url: string) => ({ platform: "Skype", url })),
      ];

  // Contact info from new contact_info_json or legacy fields
  const contactObj = content?.contact_info_json && typeof content.contact_info_json === 'object'
    ? content.contact_info_json as { phones?: string[]; emails?: string[] }
    : null;
  const phones = contactObj?.phones ?? parseJson(content?.phone_numbers, []);
  const emails = contactObj?.emails ?? parseJson(content?.email_addresses, []);

  // SEO scores from seo_analysis or computed from content
  const overallScore = seoAnalysis?.overall_score ?? (content ? Math.round(
    ((content.page_title ? 1 : 0) * 30 +
     (content.meta_description ? 1 : 0) * 30 +
     (h1Tags.length > 0 ? 1 : 0) * 20 +
     (content.total_images_count && content.images_with_alt_count
       ? (content.images_with_alt_count / content.total_images_count) * 20 : 0))
  ) : 0);
  const titleScore = seoAnalysis?.title_score ?? (content?.page_title ? 85 : 0);
  const descriptionScore = seoAnalysis?.description_score ?? (content?.meta_description ? 60 : 0);
  const headingScore = seoAnalysis?.heading_score ?? (h1Tags.length > 0 ? 78 : 0);

  const recommendations = seoAnalysis?.recommendations
    ? parseJson(seoAnalysis.recommendations, [])
    : [
        content?.meta_description && content.meta_description.length < 120 ? "Meta description 長度建議在 120-160 字元之間" : null,
        h2Tags.length < 3 ? "建議增加更多含關鍵字的 H2 標籤" : null,
        content?.total_images_count && content.images_with_alt_count !== undefined && content.images_with_alt_count < content.total_images_count ? "部分圖片缺少 alt 屬性" : null,
        "建議增加 structured data (JSON-LD)",
      ].filter(Boolean);

  const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    pending: { label: "待處理", variant: "outline" },
    processing: { label: "處理中", variant: "secondary" },
    completed: { label: "已完成", variant: "default" },
    failed: { label: "失敗", variant: "destructive" },
  };

  const handleCopyJson = () => {
    const data = { task, content, seoAnalysis };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success("已複製 JSON");
  };

  const handleDownloadJson = () => {
    const data = { task, content, seoAnalysis };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `task_${id}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("已下載 JSON");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/tasks"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-destructive">載入失敗</h1>
            <p className="text-muted-foreground">{(queryError as Error)?.message || '無法取得任務資料'}</p>
            <p className="text-xs text-muted-foreground font-mono mt-1">Task ID: {id}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/tasks"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">任務不存在</h1>
            <p className="text-muted-foreground">找不到 ID 為 {id} 的任務</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/tasks"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">任務詳情</h1>
            <p className="text-muted-foreground font-mono text-sm">
              {task.url || task.task_id}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRetry}
            disabled={retryMutation.isPending || !task || task.status === "processing"}
          >
            {retryMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" />處理中...</>
            ) : (
              <><RotateCcw className="h-4 w-4 mr-1" />重試</>
            )}
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRescrape}
            disabled={rescrapeMutation.isPending || !task}
          >
            {rescrapeMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" />處理中...</>
            ) : (
              <><RefreshCw className="h-4 w-4 mr-1" />重新抓取</>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={handleCopyJson}>
            <Copy className="h-4 w-4 mr-1" />複製 JSON
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadJson}>
            <Download className="h-4 w-4 mr-1" />下載 JSON
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">概覽</TabsTrigger>
          {!isAlibabaProduct && <TabsTrigger value="headings">H 標籤樹</TabsTrigger>}
          {!isAlibabaProduct && <TabsTrigger value="meta">Meta 資訊</TabsTrigger>}
          {!isAlibabaProduct && <TabsTrigger value="nav">導覽結構</TabsTrigger>}
          {!isAlibabaProduct && <TabsTrigger value="social">社群連結</TabsTrigger>}
          {!isAlibabaProduct && <TabsTrigger value="contact">聯絡資訊</TabsTrigger>}
          <TabsTrigger value="html"
            disabled={!(content?.html_content || content?.html_ssr || content?.html_full || content?.desc_iframe_html || (content as any)?._html_stripped || content?.html_ssr_size || content?.html_full_size)}>
            HTML
          </TabsTrigger>
          <TabsTrigger value="md"
            disabled={!content?.markdown_content && !content?.html_full && !content?.desc_iframe_html && !(content as any)?._html_stripped}>
            MD
          </TabsTrigger>
          {isAlibabaProduct && (
            <>
              <TabsTrigger value="ali-attrs">
                規格 Attributes{detail?.alibaba_attributes?.length ? ` (${detail.alibaba_attributes.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="ali-gallery">
                圖片 Gallery{(() => {
                  try {
                    const imgs: Array<{src: string}> = content?.images_json ? (typeof content.images_json === 'string' ? JSON.parse(content.images_json as string) : content.images_json as Array<{src:string}>) : [];
                    if (!Array.isArray(imgs) || imgs.length === 0) return '';
                    const mainCount = imgs.filter(i => /_960x960|_800x800/.test(i.src)).length;
                    return ` (★${mainCount} / ${imgs.length})`;
                  } catch { return ''; }
                })()}
              </TabsTrigger>
              <TabsTrigger value="ali-iframe">iframe 描述</TabsTrigger>
              <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
            </>
          )}
          {!isAlibabaProduct && <TabsTrigger value="seo">SEO 分析</TabsTrigger>}
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          {isAlibabaProduct && content ? (
          /* ── Alibaba Product Overview ── */
          <div className="space-y-4">
            {/* Hero: Image + Product Info */}
            <div className="grid gap-4 md:grid-cols-3">
              <Card className="md:col-span-1">
                <CardContent className="p-4">
                  {(() => {
                    try {
                      const imgs: Array<{src:string;alt?:string}> = content.images_json ? (typeof content.images_json === 'string' ? JSON.parse(content.images_json as string) : content.images_json as Array<{src:string;alt?:string}>) : [];
                      // Prefer 960x960 main product images first
                      const mainImg = Array.isArray(imgs) && imgs.length > 0
                        ? (imgs.find(i => /_960x960|_800x800/.test(i.src)) || imgs.find(i => i.src && !i.src.includes('svg')) || imgs[0])
                        : null;
                      return mainImg ? (
                        <img src={mainImg.src} alt={mainImg.alt || 'Product'} className="w-full aspect-square object-contain rounded-lg bg-white" />
                      ) : (
                        <div className="w-full aspect-square bg-muted rounded-lg flex items-center justify-center text-muted-foreground">無圖片</div>
                      );
                    } catch { return <div className="w-full aspect-square bg-muted rounded-lg" />; }
                  })()}
                </CardContent>
              </Card>
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg leading-snug">
                    {(() => {
                      try {
                        const schema = content.schema_markup_json ? (typeof content.schema_markup_json === 'string' ? JSON.parse(content.schema_markup_json as string) : content.schema_markup_json) : [];
                        const product = Array.isArray(schema) && schema.length > 0 ? (Array.isArray(schema[0]) ? schema[0][0] : schema[0]) : null;
                        return product?.name || content.title || '產品名稱';
                      } catch { return content.title || '產品名稱'; }
                    })()}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(() => {
                    try {
                      const schema = content.schema_markup_json ? (typeof content.schema_markup_json === 'string' ? JSON.parse(content.schema_markup_json as string) : content.schema_markup_json) : [];
                      const product = Array.isArray(schema) && schema.length > 0 ? (Array.isArray(schema[0]) ? schema[0][0] : schema[0]) : null;
                      const brand = product?.brand?.name || null;
                      const sku = product?.sku || product?.mpn || null;
                      const price = product?.offers?.price || product?.offers?.lowPrice || null;
                      const currency = product?.offers?.priceCurrency || 'USD';
                      return (
                        <>
                          {brand && <div className="flex justify-between"><span className="text-muted-foreground">品牌 Brand</span><span className="font-medium">{brand}</span></div>}
                          {sku && <div className="flex justify-between"><span className="text-muted-foreground">SKU</span><span className="font-mono text-sm">{sku}</span></div>}
                          {price && <div className="flex justify-between"><span className="text-muted-foreground">價格 Price</span><span className="text-lg font-bold text-green-400">{currency} {price}</span></div>}
                        </>
                      );
                    } catch { return null; }
                  })()}
                  {task.target_keyword && <div className="flex justify-between"><span className="text-muted-foreground">搜尋關鍵字</span><span>{task.target_keyword}</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">HTML 大小</span><span>{content.html_content ? `${((content.html_content as string).length / 1024).toFixed(0)} KB` : '—'}</span></div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">成功策略</span>
                    <span>{task.success_strategy ? getStrategyLabel(task.success_strategy) : '—'}</span>
                  </div>
                  <div className="pt-2 border-t">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><Tag className="h-3 w-3" />Tags</div>
                    <TaskTags taskId={task.task_id} />
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* Quick Attributes Preview */}
            {detail?.alibaba_attributes && detail.alibaba_attributes.length > 0 && (
              <Card>
                <CardHeader><CardTitle>規格摘要 Key Attributes ({detail.alibaba_attributes.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {detail.alibaba_attributes.slice(0, 8).map((attr, i) => (
                      <div key={i} className="p-2 rounded bg-muted/50 text-sm">
                        <div className="text-muted-foreground text-xs">{attr.key}</div>
                        <div className="font-medium truncate" title={attr.value}>{attr.value}</div>
                      </div>
                    ))}
                  </div>
                  {detail.alibaba_attributes.length > 8 && (
                    <p className="text-xs text-muted-foreground mt-2">+{detail.alibaba_attributes.length - 8} 更多規格，見「規格 Attributes」頁籤</p>
                  )}
                </CardContent>
              </Card>
            )}
            {/* Price Tiers + Variations + Lead Time */}
            {detail?.alibaba_modules && (
              <div className="grid gap-4 md:grid-cols-2">
                {/* Price Tiers */}
                {detail.alibaba_modules.price_tiers.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">價格階梯 Price Tiers</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-2">
                        {detail.alibaba_modules.price_tiers.map((t, i) => (
                          <div key={i} className="p-2 rounded bg-muted/50 text-sm">
                            <div className="text-muted-foreground text-xs">{t.quantity}</div>
                            <div className="font-bold text-green-400">{t.price}</div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {/* Variations */}
                {detail.alibaba_modules.variations.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">SKU 選項 Variations</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {detail.alibaba_modules.variations.map((v, i) => (
                        <div key={i}>
                          <div className="text-xs text-muted-foreground mb-1">{v.name}</div>
                          <div className="flex flex-wrap gap-1">
                            {v.options.slice(0, 10).map((opt, j) => (
                              <Badge key={j} variant="outline" className="text-xs">{opt}</Badge>
                            ))}
                            {v.options.length > 10 && <Badge variant="secondary" className="text-xs">+{v.options.length - 10}</Badge>}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                {/* Lead Time */}
                {detail.alibaba_modules.lead_time.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">交期 Lead Time</CardTitle></CardHeader>
                    <CardContent>
                      <div className="border rounded overflow-hidden">
                        <table className="w-full text-sm">
                          <thead><tr className="bg-muted/50"><th className="p-2 text-left">數量</th><th className="p-2 text-left">天數</th></tr></thead>
                          <tbody>
                            {detail.alibaba_modules.lead_time.map((r, i) => (
                              <tr key={i} className={i % 2 === 0 ? '' : 'bg-muted/20'}><td className="p-2">{r.quantity}</td><td className="p-2 font-medium">{r.days}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {/* Company + Customization + Highlights */}
                <Card>
                  <CardHeader><CardTitle className="text-base">供應商 & 客製化</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {detail.alibaba_modules.company && (
                      <div className="flex items-center gap-2 p-2 rounded bg-muted/50">
                        <span className="font-medium">{detail.alibaba_modules.company.name}</span>
                        {detail.alibaba_modules.company.years && <Badge variant="secondary" className="text-xs">{detail.alibaba_modules.company.years} yrs</Badge>}
                        {detail.alibaba_modules.company.country && <Badge variant="outline" className="text-xs">{detail.alibaba_modules.company.country}</Badge>}
                      </div>
                    )}
                    {detail.alibaba_modules.customization.length > 0 && (
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">客製化選項</div>
                        {detail.alibaba_modules.customization.map((c, i) => (
                          <div key={i} className="text-sm">{c.name} <span className="text-muted-foreground text-xs">({c.detail})</span></div>
                        ))}
                      </div>
                    )}
                    {detail.alibaba_modules.highlights.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {detail.alibaba_modules.highlights.map((h, i) => (
                          <Badge key={i} variant="default" className="text-xs">{h}</Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
            {/* Parse Diagnostics */}
            {detail?.alibaba_modules && (() => {
              const m = detail.alibaba_modules!;
              const total = 7;
              const failed = [m.attributes, m.price_tiers, m.variations, m.lead_time, m.customization, m.highlights].filter(a => a.length === 0).length + (m.company ? 0 : 1);
              const failRate = failed / total;
              return (
              <>
              {failRate >= 0.5 && (
                <div className="p-3 rounded-lg border border-red-500/50 bg-red-500/10 text-sm">
                  <span className="font-bold text-red-400">⚠ 解析異常警示</span>
                  <span className="ml-2 text-red-300">{failed}/{total} 個模組未解析到資料。阿里巴巴可能已變更頁面結構（CSS class / ID），建議檢查抓取策略。</span>
                </div>
              )}
              <Card>
                <CardHeader><CardTitle className="text-base">解析診斷 Parse Diagnostics</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    {[
                      { name: 'Attributes', count: detail.alibaba_modules.attributes.length, css: 'module_attribute / #key-attributes' },
                      { name: 'Price Tiers', count: detail.alibaba_modules.price_tiers.length, css: 'module_price / .price-item' },
                      { name: 'Variations', count: detail.alibaba_modules.variations.length, css: 'module_sku' },
                      { name: 'Lead Time', count: detail.alibaba_modules.lead_time.length, css: 'module_lead' },
                      { name: 'Company', count: detail.alibaba_modules.company ? 1 : 0, css: 'module_company / .company-name' },
                      { name: 'Customization', count: detail.alibaba_modules.customization.length, css: 'module_other_customization' },
                      { name: 'Highlights', count: detail.alibaba_modules.highlights.length, css: 'module_product_highlights' },
                    ].map((m, i) => (
                      <div key={i} className={`p-2 rounded border ${m.count > 0 ? 'border-green-500/30 bg-green-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
                        <div className="flex items-center gap-1">
                          <span className={m.count > 0 ? 'text-green-400' : 'text-amber-400'}>{m.count > 0 ? '✓' : '!'}</span>
                          <span className="font-medium">{m.name}</span>
                        </div>
                        <div className="text-muted-foreground">{m.count > 0 ? `${m.count} 項` : '未解析到'}</div>
                        <div className="text-muted-foreground/60 font-mono mt-0.5">{m.css}</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">如果某模組顯示「未解析到」但網頁有該資料，可能是阿里巴巴更新了 CSS class/ID。</p>
                </CardContent>
              </Card>
              </>
              );
            })()}
            {/* Crawl Status */}
            <Card>
              <CardHeader><CardTitle>爬取狀態 Crawl Status</CardTitle></CardHeader>
              <CardContent>
                {(() => {
                  const ssrDone = !!(content.html_ssr_at || content.html_content || content.html_ssr);
                  const ssrSize = content.html_ssr_size || (content.html_content ? (content.html_content as string).length : 0);
                  const fullDone = !!(content.html_full_at || content.html_full);
                  const fullSize = content.html_full_size || (content.html_full ? (content.html_full as string).length : 0);
                  return (
                    <div className="grid grid-cols-2 gap-4">
                      <div className={`p-4 rounded-lg border ${ssrDone ? 'border-green-500/30 bg-green-500/5' : 'border-dashed border-muted-foreground/30'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`h-2.5 w-2.5 rounded-full ${ssrDone ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
                          <span className="font-medium text-sm">QuickScraper (SSR)</span>
                        </div>
                        <div className="text-sm text-muted-foreground">{ssrDone ? `✓ ${(ssrSize / 1024).toFixed(0)} KB` : '尚未爬取'}</div>
                      </div>
                      <div className={`p-4 rounded-lg border ${fullDone ? 'border-purple-500/30 bg-purple-500/5' : 'border-dashed border-muted-foreground/30'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`h-2.5 w-2.5 rounded-full ${fullDone ? 'bg-purple-500' : 'bg-muted-foreground/30'}`} />
                          <span className="font-medium text-sm">BrowserAct (Full)</span>
                        </div>
                        <div className="text-sm text-muted-foreground">{fullDone ? `✓ ${(fullSize / 1024).toFixed(0)} KB` : '尚未爬取'}</div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
          ) : (
          /* ── Default Overview (non-Alibaba) ── */
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>基本資訊</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">任務 ID</span>
                  <span className="font-mono text-sm">{task.task_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">URL</span>
                  <span className="font-mono text-sm truncate max-w-[300px]">{task.url}</span>
                </div>
                {task.domain && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Domain</span>
                    <span>{task.domain}</span>
                  </div>
                )}
                {task.target_keyword && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">關鍵字</span>
                    <span>{task.target_keyword}</span>
                  </div>
                )}
                {task.success_strategy && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">成功策略 Strategy</span>
                    <span>{getStrategyLabel(task.success_strategy)}{task.success_phase != null ? ` (Phase ${task.success_phase})` : ''}</span>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Tag className="h-4 w-4" />Tags 標籤</CardTitle></CardHeader>
              <CardContent>
                <TaskTags taskId={task.task_id} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>狀態與進度</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">狀態</span>
                  <Badge variant={statusConfig[task.status]?.variant || "outline"}>
                    {statusConfig[task.status]?.label || task.status}
                  </Badge>
                </div>
                {task.duration_ms && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">耗時</span>
                    <span>{(task.duration_ms / 1000).toFixed(1)}s</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">建立時間</span>
                  <span className="text-sm">{task.created_at}</span>
                </div>
                {task.completed_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">完成時間</span>
                    <span className="text-sm">{task.completed_at}</span>
                  </div>
                )}
                {task.status === "failed" && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">狀態</span>
                    <span className="text-sm text-destructive">執行失敗</span>
                  </div>
                )}
              </CardContent>
            </Card>
            {content && (<>
              <Card className="md:col-span-2">
                <CardHeader><CardTitle>抓取內容摘要</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {(content.title || content.page_title) && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">頁面標題</span>
                      <span>{content.title || content.page_title}</span>
                    </div>
                  )}
                  {(task.target_keyword || content.keyword_ref) && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">關鍵字</span>
                      <span>{task.target_keyword || content.keyword_ref}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">字數</span>
                    <span>{content.word_count ?? 0}</span>
                  </div>
                  {content.sentence_count !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">句子數</span>
                      <span>{content.sentence_count}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">HTML 大小</span>
                    <span>{content.html_content ? `${(content.html_content.length / 1024).toFixed(1)} KB` : content.raw_html_length ? `${(content.raw_html_length / 1024).toFixed(1)} KB` : "—"}</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="md:col-span-2">
                <CardHeader><CardTitle>爬取狀態 Crawl Status</CardTitle></CardHeader>
                <CardContent>
                  {(() => {
                    // Determine SSR status: html_ssr_at (alibaba) or html_content (general SERP)
                    const ssrDone = !!(content.html_ssr_at || content.html_content || content.html_ssr);
                    const ssrTime = content.html_ssr_at || (content.html_content ? content.created_at : null);
                    const ssrSize = content.html_ssr_size || (content.html_content ? content.html_content.length : (content.html_ssr ? content.html_ssr.length : 0));
                    // Determine Full status: html_full_at (alibaba) or html_full (general)
                    const fullDone = !!(content.html_full_at || content.html_full);
                    const fullTime = content.html_full_at || null;
                    const fullSize = content.html_full_size || (content.html_full ? content.html_full.length : 0);
                    return (
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-4 rounded-lg border ${ssrDone ? 'border-green-500/30 bg-green-500/5' : 'border-dashed border-muted-foreground/30'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`h-2.5 w-2.5 rounded-full ${ssrDone ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
                        <span className="font-medium text-sm">QuickScraper (SSR)</span>
                      </div>
                      {ssrDone ? (
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <div>✓ 已完成{ssrTime ? ` — ${new Date(ssrTime).toLocaleString("zh-TW")}` : ''}</div>
                          <div>{ssrSize ? `${(ssrSize / 1024).toFixed(0)} KB` : '—'}</div>
                          {task.success_strategy && ['basic_ua_rotate', 'quickscraper_standard'].includes(task.success_strategy) && (
                            <div className="text-xs font-mono mt-1">策略: {getStrategyLabel(task.success_strategy)}</div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">尚未爬取</div>
                      )}
                    </div>
                    <div className={`p-4 rounded-lg border ${fullDone ? 'border-purple-500/30 bg-purple-500/5' : 'border-dashed border-muted-foreground/30'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`h-2.5 w-2.5 rounded-full ${fullDone ? 'bg-purple-500' : 'bg-muted-foreground/30'}`} />
                        <span className="font-medium text-sm">BrowserAct (Full)</span>
                      </div>
                      {fullDone ? (
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <div>✓ 已完成{fullTime ? ` — ${new Date(fullTime).toLocaleString("zh-TW")}` : ''}</div>
                          <div>{fullSize ? `${(fullSize / 1024).toFixed(0)} KB` : '—'}</div>
                          {task.success_strategy && ['browseract_tw', 'browseract_us'].includes(task.success_strategy) && (
                            <div className="text-xs font-mono mt-1">策略: {getStrategyLabel(task.success_strategy)}</div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">尚未爬取（排程中 or 待觸發）</div>
                      )}
                    </div>
                  </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </>)}
          </div>
          )}
        </TabsContent>

        {/* Headings */}
        <TabsContent value="headings">
          <Card>
            <CardHeader><CardTitle>Heading 分析 (H1-H6)</CardTitle></CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <h3 className="font-medium mb-3">標籤樹狀結構</h3>
                  {headingTree.length > 0 ? (
                    <HeadingTree data={headingTree} />
                  ) : (
                    <p className="text-sm text-muted-foreground">無樹狀結構資料</p>
                  )}
                </div>
                <div className="space-y-4">
                  {[
                    { tag: "H1", items: h1Tags },
                    { tag: "H2", items: h2Tags },
                    { tag: "H3", items: h3Tags },
                    { tag: "H4", items: h4Tags },
                    { tag: "H5", items: h5Tags },
                    { tag: "H6", items: h6Tags },
                  ].map(({ tag, items }) => (
                    <div key={tag}>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">{tag} ({items.length})</h4>
                      {items.length > 0 ? (
                        <ul className="space-y-1">{items.map((t: string, i: number) => <li key={i} className="text-sm">• {t}</li>)}</ul>
                      ) : (
                        <p className="text-sm text-muted-foreground">無</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Meta */}
        <TabsContent value="meta">
          <Card>
            <CardHeader><CardTitle>Meta 資訊</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {(content?.title || content?.page_title) && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Page Title</h4>
                  <p>{content.title || content.page_title}</p>
                  <p className="text-xs text-muted-foreground">{(content.title || content.page_title || '').length} 字元</p>
                </div>
              )}
              {(h1Tags.length > 0 || content?.h1_title) && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">H1 Title</h4>
                  <p>{h1Tags[0] || content.h1_title}</p>
                </div>
              )}
              {content?.meta_description && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Meta Description</h4>
                  <p>{content.meta_description}</p>
                  <p className="text-xs text-muted-foreground">{content.meta_description.length} 字元</p>
                </div>
              )}
              {content?.meta_keywords && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Meta Keywords</h4>
                  <p>{content.meta_keywords}</p>
                </div>
              )}
              {content?.canonical_url && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Canonical URL</h4>
                  <p className="font-mono text-sm">{content.canonical_url}</p>
                </div>
              )}
              {content?.open_graph_json && Object.keys(typeof content.open_graph_json === 'object' ? content.open_graph_json : {}).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Open Graph</h4>
                  <pre className="text-sm bg-muted p-3 rounded overflow-auto">
                    {typeof content.open_graph_json === 'string' ? content.open_graph_json : JSON.stringify(content.open_graph_json, null, 2)}
                  </pre>
                </div>
              )}
              {!content && <p className="text-sm text-muted-foreground">尚無內容資料</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Nav */}
        <TabsContent value="nav">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-sm">新版 — nav_tree_json (樹狀結構)</CardTitle></CardHeader>
              <CardContent>
                {navItems.length > 0 ? (
                  <NavTree items={navItems} />
                ) : (
                  <p className="text-sm text-muted-foreground">尚無資料</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-sm">舊版 — navigation_bar_link (純文字+連結)</CardTitle></CardHeader>
              <CardContent>
                {content?.nav_structure || navItems.length > 0 ? (
                  <pre className="text-xs font-mono bg-muted p-3 rounded-md overflow-auto max-h-[400px] whitespace-pre-wrap">
                    {navItems.length > 0
                      ? navItems.map((item: any) => {
                          const label = item.label || item.text || item.name || '';
                          const url = item.url || item.href || '';
                          const children = item.children || [];
                          let lines = `Category: ${label}${url ? ` (${url})` : ''}\n`;
                          children.forEach((c: any) => {
                            const cl = c.label || c.text || c.name || '';
                            const cu = c.url || c.href || '';
                            lines += `  Subcategory: ${cl}${cu ? ` (${cu})` : ''}\n`;
                            (c.children || []).forEach((gc: any) => {
                              const gl = gc.label || gc.text || gc.name || '';
                              const gu = gc.url || gc.href || '';
                              lines += `    Sub-sub: ${gl}${gu ? ` (${gu})` : ''}\n`;
                            });
                          });
                          return lines;
                        }).join('')
                      : content?.nav_structure || '尚無資料'}
                  </pre>
                ) : (
                  <p className="text-sm text-muted-foreground">尚無資料</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Social */}
        <TabsContent value="social">
          <Card>
            <CardHeader><CardTitle>社群連結</CardTitle></CardHeader>
            <CardContent>
              {socialLinks.length > 0 ? (
                <div className="space-y-2">
                  {socialLinks.map((link: any, i: number) => (
                    <div key={i} className="flex items-center gap-3 p-3 border rounded-md">
                      <Badge variant="secondary">{link.platform}</Badge>
                      <span className="font-mono text-sm text-primary">{link.url}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">尚無社群連結</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact */}
        <TabsContent value="contact">
          <Card>
            <CardHeader><CardTitle>聯絡資訊</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {phones.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">電話號碼</h4>
                  {phones.map((p: string, i: number) => <p key={i} className="text-sm">{p}</p>)}
                </div>
              )}
              {emails.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Email</h4>
                  {emails.map((e: string, i: number) => <p key={i} className="text-sm">{e}</p>)}
                </div>
              )}
              {phones.length === 0 && emails.length === 0 && (
                <p className="text-sm text-muted-foreground">尚無聯絡資訊</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── HTML tab (QS / BrowserAct / P_iframe) — lazy-loaded to avoid 3MB+ response ── */}
        <TabsContent value="html">
          <div className="space-y-4">
            {/* Sub-tab switcher */}
            <div className="flex gap-2 flex-wrap">
              {([
                { key: 'qs' as const, label: 'QuickScraper', sizeBytes: (content?.html_ssr_size || 0) as number },
                { key: 'ba' as const, label: 'BrowserAct', sizeBytes: (content?.html_full_size || 0) as number },
                ...(isAlibabaProduct ? [{ key: 'iframe' as const, label: 'P_iframe', sizeBytes: 0 }] : []),
              ]).map(({ key, label, sizeBytes }) => (
                <Button key={key} variant={htmlSubTab === key ? 'default' : 'outline'} size="sm"
                  onClick={() => { setHtmlSubTab(key); setHtmlRawOpen(false); }}>
                  {label}
                  {sizeBytes > 0 && <span className="text-xs ml-1 opacity-60">({(sizeBytes / 1024).toFixed(0)} KB)</span>}
                </Button>
              ))}
            </div>
            {/* Lazy-loaded content */}
            {(() => {
              const labelMap = { qs: 'QuickScraper', ba: 'BrowserAct', iframe: 'P_iframe 產品介紹頁' };
              const dlMap = { qs: 'QS', ba: 'BA', iframe: 'iframe' };
              const tsMap = { qs: content?.html_ssr_at as string | undefined, ba: content?.html_full_at as string | undefined, iframe: undefined };
              const label = labelMap[htmlSubTab];
              const dl = dlMap[htmlSubTab];
              const ts = tsMap[htmlSubTab];
              const html = lazyHtmlData || '';
              if (htmlLoading) return <Card><CardContent className="py-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />載入 HTML 中...</CardContent></Card>;
              if (!html) return <Card><CardContent className="py-8 text-center text-muted-foreground">{label} 尚未爬取</CardContent></Card>;
              const sizeKb = (html.length / 1024).toFixed(1);
              return (
                <>
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <button className="flex items-center gap-2 text-left flex-1" onClick={() => setHtmlRawOpen(v => !v)}>
                          <CardTitle className="text-base">
                            HTML ({label})
                            <span className="text-sm font-normal text-muted-foreground ml-2">({sizeKb} KB)</span>
                            {ts && <span className="text-xs font-normal text-muted-foreground ml-2">{new Date(ts).toLocaleString("zh-TW")}</span>}
                          </CardTitle>
                          {htmlRawOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        </button>
                        <div className="flex items-center gap-2 ml-4">
                          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(html); toast.success("已複製 HTML"); }}>
                            <Copy className="h-4 w-4 mr-1" />複製
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => {
                            const blob = new Blob([html], { type: "text/html" });
                            const u = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = u; a.download = `${task.domain || "page"}_${dl}_${task.task_id.slice(0, 8)}.html`;
                            a.click(); URL.revokeObjectURL(u); toast.success("已下載 HTML");
                          }}>
                            <Download className="h-4 w-4 mr-1" />下載
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    {htmlRawOpen && (
                      <CardContent className="pt-0">
                        <pre className="bg-muted p-4 rounded-md overflow-auto max-h-[400px] text-xs font-mono whitespace-pre-wrap break-all">{html}</pre>
                      </CardContent>
                    )}
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-muted-foreground">渲染預覽</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <iframe srcDoc={html} className="w-full h-[600px] border rounded-md bg-white" sandbox="allow-scripts" title="HTML Preview" />
                    </CardContent>
                  </Card>
                </>
              );
            })()}
          </div>
        </TabsContent>

        {/* ── MD tab (QS / BrowserAct / P_iframe) ── */}
        <TabsContent value="md">
          <div className="space-y-4">
            {/* Sub-tab switcher */}
            <div className="flex gap-2 flex-wrap">
              {([
                { key: 'qs' as const, label: 'QuickScraper', md: (content?.markdown_content || '') as string },
                { key: 'ba' as const, label: 'BrowserAct', md: '' },
                ...(isAlibabaProduct ? [{ key: 'iframe' as const, label: 'P_iframe', md: (content?.desc_iframe_markdown || '') as string }] : []),
              ]).map(({ key, label, md }) => (
                <Button key={key} variant={mdSubTab === key ? 'default' : 'outline'} size="sm"
                  disabled={!md}
                  onClick={() => setMdSubTab(key)}>
                  {label}
                  {md && <span className="text-xs ml-1 opacity-60">({(md.length / 1024).toFixed(0)} KB)</span>}
                </Button>
              ))}
            </div>
            {/* Content */}
            {(() => {
              const cfgMap = {
                qs:     { md: (content?.markdown_content || '') as string, label: 'QuickScraper', dl: 'QS' },
                ba:     { md: '', label: 'BrowserAct', dl: 'BA' },
                iframe: { md: (content?.desc_iframe_markdown || '') as string, label: 'P_iframe', dl: 'iframe' },
              };
              const { md, label, dl } = cfgMap[mdSubTab];
              if (!md) return (
                <Card><CardContent className="py-8 text-center text-muted-foreground">
                  {mdSubTab === 'ba' ? 'BrowserAct Markdown 待產生（需從 html_full 轉換）' : `${label} Markdown 尚未產生`}
                </CardContent></Card>
              );
              return (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Markdown ({label})</CardTitle>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(md); toast.success("已複製 Markdown"); }}>
                          <Copy className="h-4 w-4 mr-1" />複製
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => {
                          const blob = new Blob([md], { type: "text/markdown" });
                          const u = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = u; a.download = `${task.domain || "page"}_${dl}_${task.task_id.slice(0, 8)}.md`;
                          a.click(); URL.revokeObjectURL(u); toast.success("已下載 Markdown");
                        }}>
                          <Download className="h-4 w-4 mr-1" />下載
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <pre className="bg-muted p-4 rounded-md overflow-auto max-h-[600px] text-sm font-mono whitespace-pre-wrap">{md}</pre>
                  </CardContent>
                </Card>
              );
            })()}
          </div>
        </TabsContent>

        {/* (legacy tab shells — no longer visible in TabsList) */}
        <TabsContent value="html-qs">
          {(() => {
            const html = content?.html_ssr || content?.html_content || '';
            const sizeKb = html ? (html.length / 1024).toFixed(1) : null;
            return html ? (
              <div className="space-y-4">
                {/* Accordion: Raw HTML */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <button className="flex items-center gap-2 text-left flex-1" onClick={() => setHtmlQsOpen(v => !v)}>
                        <CardTitle className="text-base">
                          HTML (QuickScraper)
                          {sizeKb && <span className="text-sm font-normal text-muted-foreground ml-2">({sizeKb} KB)</span>}
                          {content?.html_ssr_at && <span className="text-xs font-normal text-muted-foreground ml-2">{new Date(content.html_ssr_at).toLocaleString("zh-TW")}</span>}
                        </CardTitle>
                        {htmlQsOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </button>
                      <div className="flex items-center gap-2 ml-4">
                        <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(html); toast.success("已複製 HTML"); }}>
                          <Copy className="h-4 w-4 mr-1" />複製
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => {
                          const blob = new Blob([html], { type: "text/html" });
                          const u = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = u; a.download = `${task.domain || "page"}_QS_${task.task_id.slice(0, 8)}.html`;
                          a.click(); URL.revokeObjectURL(u); toast.success("已下載 HTML");
                        }}>
                          <Download className="h-4 w-4 mr-1" />下載
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  {htmlQsOpen && (
                    <CardContent className="pt-0">
                      <pre className="bg-muted p-4 rounded-md overflow-auto max-h-[400px] text-xs font-mono whitespace-pre-wrap break-all">{html}</pre>
                    </CardContent>
                  )}
                </Card>
                {/* Rendered Preview */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-muted-foreground">渲染預覽</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <iframe srcDoc={html} className="w-full h-[600px] border rounded-md bg-white" sandbox="allow-scripts" title="QS HTML Preview" />
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card><CardContent className="py-8 text-center text-muted-foreground">QuickScraper 尚未爬取</CardContent></Card>
            );
          })()}
        </TabsContent>

        {/* MD (QuickScraper) */}
        <TabsContent value="md-qs">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Markdown (QuickScraper)</CardTitle>
                {content?.markdown_content && (
                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(content.markdown_content || ""); toast.success("已複製 Markdown"); }}>
                    <Copy className="h-4 w-4 mr-1" />複製
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {content?.markdown_content ? (
                <pre className="bg-muted p-4 rounded-md overflow-auto max-h-[500px] text-sm font-mono whitespace-pre-wrap">
                  {content.markdown_content}
                </pre>
              ) : (
                <div className="text-center py-8 text-muted-foreground">QuickScraper 尚未爬取</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* HTML (BrowserAct) */}
        <TabsContent value="html-ba">
          {(() => {
            const html = content?.html_full || '';
            const sizeKb = html ? (html.length / 1024).toFixed(1) : null;
            return html ? (
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <button className="flex items-center gap-2 text-left flex-1" onClick={() => setHtmlBaOpen(v => !v)}>
                        <CardTitle className="text-base">
                          HTML (BrowserAct)
                          {sizeKb && <span className="text-sm font-normal text-muted-foreground ml-2">({sizeKb} KB)</span>}
                          {content?.html_full_at && <span className="text-xs font-normal text-muted-foreground ml-2">{new Date(content.html_full_at).toLocaleString("zh-TW")}</span>}
                        </CardTitle>
                        {htmlBaOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </button>
                      <div className="flex items-center gap-2 ml-4">
                        <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(html); toast.success("已複製 HTML"); }}>
                          <Copy className="h-4 w-4 mr-1" />複製
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => {
                          const blob = new Blob([html], { type: "text/html" });
                          const u = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = u; a.download = `${task.domain || "page"}_BA_${task.task_id.slice(0, 8)}.html`;
                          a.click(); URL.revokeObjectURL(u); toast.success("已下載 HTML");
                        }}>
                          <Download className="h-4 w-4 mr-1" />下載
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  {htmlBaOpen && (
                    <CardContent className="pt-0">
                      <pre className="bg-muted p-4 rounded-md overflow-auto max-h-[400px] text-xs font-mono whitespace-pre-wrap break-all">{html}</pre>
                    </CardContent>
                  )}
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-muted-foreground">渲染預覽</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <iframe srcDoc={html} className="w-full h-[600px] border rounded-md bg-white" sandbox="allow-scripts" title="BA HTML Preview" />
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card><CardContent className="py-8 text-center text-muted-foreground">BrowserAct 尚未爬取（深度爬取待排程）</CardContent></Card>
            );
          })()}
        </TabsContent>

        {/* MD (BrowserAct) */}
        <TabsContent value="md-ba">
          <Card>
            <CardHeader>
              <CardTitle>Markdown (BrowserAct)</CardTitle>
            </CardHeader>
            <CardContent>
              {content?.html_full ? (
                <p className="text-sm text-muted-foreground">BrowserAct Markdown 待產生（需從 html_full 轉換）</p>
              ) : (
                <div className="text-center py-8 text-muted-foreground">BrowserAct 尚未爬取（深度爬取待排程）</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* HTML (iframe) */}
        <TabsContent value="html-iframe">
          {(() => {
            const html = (content?.desc_iframe_html as string) || '';
            const sizeKb = html ? (html.length / 1024).toFixed(1) : null;
            return html ? (
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <button className="flex items-center gap-2 text-left flex-1" onClick={() => setHtmlIframeOpen(v => !v)}>
                        <CardTitle className="text-base">
                          HTML (iframe 產品介紹頁)
                          {sizeKb && <span className="text-sm font-normal text-muted-foreground ml-2">({sizeKb} KB)</span>}
                          {content?.desc_iframe_url && (
                            <a href={content.desc_iframe_url as string} target="_blank" rel="noopener noreferrer"
                              className="text-xs font-normal text-blue-500 ml-2 hover:underline" onClick={e => e.stopPropagation()}>
                              原始來源 ↗
                            </a>
                          )}
                        </CardTitle>
                        {htmlIframeOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </button>
                      <div className="flex items-center gap-2 ml-4">
                        <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(html); toast.success("已複製 HTML"); }}>
                          <Copy className="h-4 w-4 mr-1" />複製
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => {
                          const blob = new Blob([html], { type: "text/html" });
                          const u = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = u; a.download = `${task.domain || "page"}_iframe_${task.task_id.slice(0, 8)}.html`;
                          a.click(); URL.revokeObjectURL(u); toast.success("已下載 HTML");
                        }}>
                          <Download className="h-4 w-4 mr-1" />下載
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  {htmlIframeOpen && (
                    <CardContent className="pt-0">
                      <pre className="bg-muted p-4 rounded-md overflow-auto max-h-[400px] text-xs font-mono whitespace-pre-wrap break-all">{html}</pre>
                    </CardContent>
                  )}
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-muted-foreground">渲染預覽（產品介紹）</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <iframe srcDoc={html} className="w-full h-[600px] border rounded-md bg-white" sandbox="allow-scripts" title="iframe HTML Preview" />
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card><CardContent className="py-8 text-center text-muted-foreground">iframe 頁面 HTML 尚未爬取</CardContent></Card>
            );
          })()}
        </TabsContent>

        {/* MD (iframe) */}
        <TabsContent value="md-iframe">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Markdown (iframe 產品介紹頁)</CardTitle>
                {(content?.desc_iframe_markdown as string) && (
                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(content!.desc_iframe_markdown as string); toast.success("已複製 Markdown"); }}>
                    <Copy className="h-4 w-4 mr-1" />複製
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {(content?.desc_iframe_markdown as string) ? (
                <pre className="bg-muted p-4 rounded-md overflow-auto max-h-[600px] text-sm font-mono whitespace-pre-wrap">
                  {content!.desc_iframe_markdown as string}
                </pre>
              ) : content?.desc_iframe_html ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>iframe Markdown 待產生</p>
                  <p className="text-xs mt-1">已有 iframe HTML，需後端轉換 Markdown</p>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">iframe 頁面尚未爬取</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alibaba Product — placeholder tabs */}
        {isAlibabaProduct && (
          <>
            <TabsContent value="ali-attrs">
              <Card>
                <CardHeader>
                  <CardTitle>
                    規格 Attributes
                    {detail?.alibaba_attributes?.length ? (
                      <Badge variant="secondary" className="ml-2">{detail.alibaba_attributes.length}</Badge>
                    ) : null}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {detail?.alibaba_attributes && detail.alibaba_attributes.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="text-left p-3 font-medium w-1/3 border-b">Attribute</th>
                            <th className="text-left p-3 font-medium border-b">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.alibaba_attributes.map((attr, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                              <td className="p-3 border-b text-muted-foreground font-medium">{attr.key}</td>
                              <td className="p-3 border-b">{attr.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      無規格資料（可能需要 BrowserAct 深度爬取）
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="ali-gallery">
              {(() => {
                type ImgItem = { src: string; alt?: string; width?: number; height?: number }
                let images: ImgItem[] = [];
                try {
                  const raw = content?.images_json;
                  if (raw) images = typeof raw === 'string' ? JSON.parse(raw) : raw as ImgItem[];
                } catch { /* ignore */ }

                // Categorize by Alibaba CDN URL size suffix
                // 960x960 = main product carousel  |  350/300x350/300 = related/similar products
                const mainImgs    = images.filter(i => /_960x960|_800x800/.test(i.src))
                const relatedImgs = images.filter(i => /_350x350|_300x300|_220x220/.test(i.src))
                const otherImgs   = images.filter(i =>
                  !/_960x960|_800x800|_350x350|_300x300|_220x220/.test(i.src)
                )

                const renderGrid = (imgs: ImgItem[], cols: string, imgClass: string) => (
                  <div className={`grid ${cols} gap-3`}>
                    {imgs.map((img, idx) => (
                      <div key={idx} className="group relative rounded-lg overflow-hidden border border-border bg-muted/30">
                        <div className="aspect-square overflow-hidden">
                          <img
                            src={img.src}
                            alt={img.alt || `image ${idx + 1}`}
                            className={`w-full h-full object-cover transition-transform duration-200 group-hover:scale-105 ${imgClass}`}
                            loading="lazy"
                            onError={e => { (e.target as HTMLImageElement).style.opacity = '0.3' }}
                          />
                        </div>
                        <div className="p-1.5 space-y-1">
                          {img.alt && (
                            <p className="text-xs text-muted-foreground truncate" title={img.alt}>{img.alt}</p>
                          )}
                          <Button
                            variant="ghost" size="sm"
                            className="w-full h-6 text-xs"
                            onClick={() => { navigator.clipboard.writeText(img.src); toast.success('URL 已複製'); }}
                          >
                            <Copy className="h-3 w-3 mr-1" />複製
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )

                if (!Array.isArray(images) || images.length === 0) {
                  return (
                    <Card>
                      <CardHeader><CardTitle className="flex items-center gap-2"><ImageIcon className="h-5 w-5" />圖片 Gallery</CardTitle></CardHeader>
                      <CardContent><div className="text-center py-8 text-muted-foreground">無圖片資料</div></CardContent>
                    </Card>
                  )
                }

                return (
                  <div className="space-y-6">
                    {/* ── Section 1: Main product images ── */}
                    <Card className="border-2 border-primary/40">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-base">
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/15 text-primary text-sm font-semibold">
                            ★ 主商品圖
                          </span>
                          <span className="text-muted-foreground font-normal text-sm">
                            ({mainImgs.length} 張 · 960×960 高清)
                          </span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {mainImgs.length > 0
                          ? renderGrid(mainImgs, 'grid-cols-3 md:grid-cols-4 lg:grid-cols-6', '')
                          : <p className="text-sm text-muted-foreground py-4 text-center">無主商品圖</p>
                        }
                      </CardContent>
                    </Card>

                    {/* ── Section 2: Related/similar recommendations ── */}
                    {relatedImgs.length > 0 && (
                      <Card className="border border-orange-500/30">
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center gap-2 text-base">
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-500 text-sm font-semibold">
                              相關推薦商品
                            </span>
                            <span className="text-muted-foreground font-normal text-sm">
                              ({relatedImgs.length} 張 · 350/300px 縮圖)
                            </span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {renderGrid(relatedImgs, 'grid-cols-4 md:grid-cols-6 lg:grid-cols-8', 'opacity-80')}
                        </CardContent>
                      </Card>
                    )}

                    {/* ── Section 3: Others ── */}
                    {otherImgs.length > 0 && (
                      <Card className="border border-border/50 opacity-70">
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center gap-2 text-base">
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-sm font-normal">
                              其他圖片
                            </span>
                            <span className="text-muted-foreground font-normal text-sm">
                              ({otherImgs.length} 張 · Logo / 認證 / 說明圖等)
                            </span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {renderGrid(otherImgs, 'grid-cols-4 md:grid-cols-6 lg:grid-cols-8', 'opacity-70')}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )
              })()}
            </TabsContent>
            <TabsContent value="ali-iframe">
              {content && <IframeTab content={content as Record<string, unknown>} taskId={task.task_id} productUrl={task.url} />}
            </TabsContent>
          </>
        )}

        {/* Pipeline (Alibaba only) */}
        {isAlibabaProduct && (
          <TabsContent value="pipeline">
            <PipelineTab taskId={id!} />
          </TabsContent>
        )}

        {/* SEO */}
        <TabsContent value="seo">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>SEO 評分</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <span className="text-5xl font-bold">{overallScore}</span>
                  <span className="text-2xl text-muted-foreground">/100</span>
                </div>
                <ScoreBar label="標題評分" score={titleScore} />
                <ScoreBar label="描述評分" score={descriptionScore} />
                <ScoreBar label="標題結構評分" score={headingScore} />
                {content && (
                  <div className="pt-4 border-t space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">字數</span><span>{content.word_count || 0}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">內部連結</span><span>{content.internal_links_count || 0}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">外部連結</span><span>{content.external_links_count || 0}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">圖片總數</span><span>{content.total_images_count || 0}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">有 Alt 的圖片</span><span>{content.images_with_alt_count || 0}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>改善建議</CardTitle></CardHeader>
              <CardContent>
                {recommendations.length > 0 ? (
                  <ul className="space-y-3">
                    {recommendations.map((rec: string, i: number) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <span className="text-warning mt-0.5">⚠</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">尚無 SEO 分析資料</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
